import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import {
  Title,
  Stack,
  Group,
  Select,
  TextInput,
  PasswordInput,
  Alert,
  Badge,
  Text,
  Modal,
  Loader,
  Center,
  Divider,
  Table,
  Paper,
  Button,
  Checkbox,
  ActionIcon,
  Tooltip,
  Tabs,
  SimpleGrid,
} from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { DataTable } from 'mantine-datatable'
import { IconAlertCircle, IconAlertTriangle, IconSearch, IconDatabaseOff, IconUserPlus, IconLockOpen, IconUserOff, IconUserCheck, IconPlus, IconTrash, IconRotateClockwise, IconKey } from '@tabler/icons-react'
import { useUsersStore } from '../../stores/usersStore'
import { useApplicationsStore } from '../../stores/applicationsStore'
import { getUser, getErrorMessage, createUser, createAppProfile, updateAppProfile, updateUser, unlockUser } from '../../api/usersApi'
import { listClients } from '../../api/clientsApi'
import type { Client } from '../../api/clientsApi'
import type { UserStatus, AppProfile, AccessScopes } from '@paperair/shared-types'
import type { UserWithAppCount, UserDetailResponse } from '../../api/usersApi'
import { ScopeEditor } from '../../components/ScopeEditor'
import { getUserLoginActivity } from '../../api/loginActivityApi'
import type { UserLoginActivityResponse } from '../../api/loginActivityApi'
import { LoginActivityPanel } from './LoginActivityPanel'
import { UserPasskeysSection } from './UserPasskeysSection'

const PAGE_SIZES = [10, 20, 50, 100]

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'locked', label: 'Locked' },
]

const STATUS_COLORS: Record<UserStatus, string> = {
  pending: 'yellow',
  active: 'green',
  inactive: 'gray',
  locked: 'red',
}

function formatDate(date: Date | string | undefined): string {
  if (!date) return 'Never'
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function getUserName(user: UserWithAppCount): string {
  const { firstName, lastName, displayName } = user.profile
  if (displayName) return displayName
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ')
  }
  return '—'
}

export function UsersView() {
  const {
    users,
    total,
    page,
    pageSize,
    isLoading,
    error,
    statusFilter,
    sortBy,
    sortDir,
    fetchUsers,
    setPage,
    setPageSize,
    setStatusFilter,
    setSearchQuery,
    setSort,
    clearError,
  } = useUsersStore()

  const [searchInput, setSearchInput] = useState(() => useUsersStore.getState().searchQuery)
  const [debouncedSearch] = useDebouncedValue(searchInput, 300)

  // Applications store for allowedRoles lookup
  const { applications, fetchApplications: fetchApps } = useApplicationsStore()

  // User detail modal state
  const [modalOpened, { open: openModal, close: closeModalBase }] = useDisclosure(false)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [userDetail, setUserDetail] = useState<UserDetailResponse | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [updatingProfileId, setUpdatingProfileId] = useState<string | null>(null)
  const [unlocking, setUnlocking] = useState(false)
  const [disabling, setDisabling] = useState(false)
  const [showDisableReason, setShowDisableReason] = useState(false)
  const [disableReason, setDisableReason] = useState('')
  const [clients, setClients] = useState<Client[]>([])
  const [loginActivity, setLoginActivity] = useState<UserLoginActivityResponse | null>(null)
  const [loginActivityLoading, setLoginActivityLoading] = useState(false)
  const [loginActivityError, setLoginActivityError] = useState<string | null>(null)

  // Add-application inline picker state (within the user detail modal)
  const [addingApp, setAddingApp] = useState(false)
  const [newAppId, setNewAppId] = useState<string | null>(null)
  const [newAppRole, setNewAppRole] = useState<string | null>(null)
  const [addAppLoading, setAddAppLoading] = useState(false)

  // Reset selectedUserId on close so re-clicking same row triggers fetch
  const closeModal = useCallback(() => {
    closeModalBase()
    setSelectedUserId(null)
    setShowDisableReason(false)
    setDisableReason('')
    setAddingApp(false)
    setNewAppId(null)
    setNewAppRole(null)
    setLoginActivity(null)
    setLoginActivityError(null)
  }, [closeModalBase])

  // Auto-open the detail modal when linked to from elsewhere (e.g. the dashboard's Flagged
  // Accounts card) via ?userId=
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const linkedUserId = searchParams.get('userId')
    if (linkedUserId) {
      setSelectedUserId(linkedUserId)
      openModal()
      setSearchParams((prev) => {
        prev.delete('userId')
        return prev
      }, { replace: true })
    }
  }, [searchParams, setSearchParams, openModal])

  // Create user modal state
  const [createUserOpened, { open: openCreateUser, close: closeCreateUserBase }] = useDisclosure(false)
  const [createUserLoading, setCreateUserLoading] = useState(false)
  const [createUserError, setCreateUserError] = useState<string | null>(null)
  // Per-app role selections: appId -> role (empty string = not selected)
  const [appSelections, setAppSelections] = useState<Record<string, { selected: boolean; role: string }>>({})

  const closeCreateUser = useCallback(() => {
    closeCreateUserBase()
    setCreateUserError(null)
    setAppSelections({})
    createUserForm.reset()
  }, [closeCreateUserBase])

  const handleOpenCreateUser = useCallback(() => {
    setAppSelections({})
    fetchApps(true)
    openCreateUser()
  }, [fetchApps, openCreateUser])

  const createUserForm = useForm({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      organization: '',
      jobTitle: '',
      password: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      firstName: (value) => (value.trim().length < 1 ? 'First name is required' : null),
      lastName: (value) => (value.trim().length < 1 ? 'Last name is required' : null),
      password: (value) => {
        if (value.length < 8) return 'Password must be at least 8 characters'
        if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter'
        if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter'
        if (!/[0-9]/.test(value)) return 'Password must contain a number'
        return null
      },
    },
  })

  const handleCreateUserSubmit = createUserForm.onSubmit(async (values) => {
    setCreateUserLoading(true)
    setCreateUserError(null)

    const apps = Object.entries(appSelections)
      .filter(([, sel]) => sel.selected && sel.role)
      .map(([appId, sel]) => ({ appId, role: sel.role }))

    try {
      await createUser({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        organization: values.organization || undefined,
        jobTitle: values.jobTitle || undefined,
        password: values.password,
        apps,
      })
      notifications.show({
        title: 'User Created',
        message: `${values.email} has been created and must change their password on first login.`,
        color: 'green',
      })
      closeCreateUser()
      fetchUsers()
    } catch (err) {
      setCreateUserError(getErrorMessage(err))
    } finally {
      setCreateUserLoading(false)
    }
  })

  // Fetch user detail and applications when selected
  useEffect(() => {
    if (!selectedUserId) return

    let cancelled = false
    setDetailLoading(true)
    setDetailError(null)
    setLoginActivityLoading(true)
    setLoginActivityError(null)

    // Fetch user details, applications, and clients in parallel
    Promise.all([
      getUser(selectedUserId),
      fetchApps(true),
      listClients({ isActive: 'true', limit: 500 }),
    ])
      .then(([userData, , clientsData]) => {
        if (!cancelled) {
          setUserDetail(userData)
          setClients(clientsData.clients)
          setDetailLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDetailError(getErrorMessage(err))
          setDetailLoading(false)
        }
      })

    getUserLoginActivity(selectedUserId)
      .then((activity) => {
        if (!cancelled) {
          setLoginActivity(activity)
          setLoginActivityLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoginActivityError(getErrorMessage(err))
          setLoginActivityLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [selectedUserId, fetchApps])

  // Build a map of appId -> allowedRoles for quick lookup
  const appRolesMap = useMemo(() => {
    const map = new Map<string, string[]>()
    applications.forEach((app) => {
      map.set(app.appId, app.allowedRoles || [])
    })
    return map
  }, [applications])

  // Check if a role is valid for an app
  const isRoleValid = useCallback(
    (appId: string, role: string): boolean => {
      const allowedRoles = appRolesMap.get(appId)
      if (!allowedRoles || allowedRoles.length === 0) return true // No restrictions
      return allowedRoles.includes(role)
    },
    [appRolesMap]
  )

  // Get role options for an app (including current role if invalid)
  const getRoleOptions = useCallback(
    (appId: string, currentRole: string): { value: string; label: string }[] => {
      const allowedRoles = appRolesMap.get(appId) || []
      const options = allowedRoles.map((role) => ({ value: role, label: role }))

      // If current role is not in allowed roles, add it with a warning indicator
      if (currentRole && !allowedRoles.includes(currentRole)) {
        options.unshift({ value: currentRole, label: `${currentRole} (invalid)` })
      }

      return options
    },
    [appRolesMap]
  )

  // Handle role change for an app profile
  const handleRoleChange = useCallback(
    async (profile: AppProfile, newRole: string) => {
      if (newRole === profile.role || !newRole) return

      setUpdatingProfileId(profile._id)
      try {
        await updateAppProfile(profile._id, { role: newRole })
        // Update local state
        setUserDetail((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            appProfiles: prev.appProfiles.map((p) =>
              p._id === profile._id ? { ...p, role: newRole } : p
            ),
          }
        })
        notifications.show({
          title: 'Role Updated',
          message: `Role changed to "${newRole}" for ${profile.appId}`,
          color: 'green',
        })
      } catch (err) {
        notifications.show({
          title: 'Error',
          message: getErrorMessage(err),
          color: 'red',
        })
      } finally {
        setUpdatingProfileId(null)
      }
    },
    []
  )

  // Apps the user already has a profile for (active or inactive) — excluded from the add picker
  const assignedAppIds = useMemo(() => {
    if (!userDetail) return new Set<string>()
    return new Set(userDetail.appProfiles.map((p) => p.appId))
  }, [userDetail])

  const availableAppsToAdd = useMemo(
    () => applications.filter((app) => app.isActive && !assignedAppIds.has(app.appId)),
    [applications, assignedAppIds]
  )

  const handleOpenAddApp = useCallback(() => {
    setNewAppId(null)
    setNewAppRole(null)
    setAddingApp(true)
  }, [])

  const handleCancelAddApp = useCallback(() => {
    setAddingApp(false)
    setNewAppId(null)
    setNewAppRole(null)
  }, [])

  const handleSelectNewApp = useCallback(
    (appId: string | null) => {
      setNewAppId(appId)
      const app = applications.find((a) => a.appId === appId)
      setNewAppRole(app?.defaultNewUserRole || app?.allowedRoles?.[0] || null)
    },
    [applications]
  )

  const handleConfirmAddApp = useCallback(async () => {
    if (!userDetail || !newAppId || !newAppRole) return

    setAddAppLoading(true)
    try {
      const appProfile = await createAppProfile(userDetail.user._id, {
        appId: newAppId,
        role: newAppRole,
      })
      setUserDetail((prev) =>
        prev ? { ...prev, appProfiles: [...prev.appProfiles, appProfile] } : prev
      )
      notifications.show({
        title: 'Application Added',
        message: `${newAppId} has been assigned to ${userDetail.user.email}.`,
        color: 'green',
      })
      setAddingApp(false)
      setNewAppId(null)
      setNewAppRole(null)
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setAddAppLoading(false)
    }
  }, [userDetail, newAppId, newAppRole])

  // Flip an app profile's active state (remove = deactivate, reactivate = activate)
  const handleToggleProfileActive = useCallback(
    async (profile: AppProfile, isActive: boolean) => {
      setUpdatingProfileId(profile._id)
      try {
        const updated = await updateAppProfile(profile._id, { isActive })
        setUserDetail((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            appProfiles: prev.appProfiles.map((p) =>
              p._id === profile._id ? { ...p, isActive: updated.isActive } : p
            ),
          }
        })
        notifications.show({
          title: isActive ? 'Application Reactivated' : 'Application Removed',
          message: isActive
            ? `${profile.appId} has been reactivated.`
            : `${profile.appId} access has been revoked.`,
          color: isActive ? 'green' : 'orange',
        })
      } catch (err) {
        notifications.show({
          title: 'Error',
          message: getErrorMessage(err),
          color: 'red',
        })
      } finally {
        setUpdatingProfileId(null)
      }
    },
    []
  )

  const handleRemoveApp = useCallback(
    (profile: AppProfile) => {
      modals.openConfirmModal({
        title: 'Remove Application',
        children: (
          <Text size="sm">
            Are you sure you want to remove <strong>{profile.appId}</strong> from this user?
            This will revoke their access to the application. It can be reactivated later from
            this same table.
          </Text>
        ),
        labels: { confirm: 'Remove', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => handleToggleProfileActive(profile, false),
      })
    },
    [handleToggleProfileActive]
  )

  const handleScopesSave = useCallback(
    async (newScopes: AccessScopes) => {
      if (!userDetail) return
      await updateUser(userDetail.user._id, { accessScopes: newScopes })
      setUserDetail((prev) =>
        prev ? { ...prev, user: { ...prev.user, accessScopes: newScopes } } : prev
      )
    },
    [userDetail]
  )

  const handleUnlock = useCallback(async () => {
    if (!userDetail) return
    setUnlocking(true)
    try {
      const updatedUser = await unlockUser(userDetail.user._id)
      setUserDetail((prev) => prev ? { ...prev, user: updatedUser } : prev)
      fetchUsers()
      notifications.show({
        title: 'Account Unlocked',
        message: `${userDetail.user.email} has been unlocked.`,
        color: 'green',
      })
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setUnlocking(false)
    }
  }, [userDetail, fetchUsers])

  const handleDisable = useCallback(async () => {
    if (!userDetail) return
    setDisabling(true)
    try {
      const updatedUser = await updateUser(userDetail.user._id, {
        status: 'inactive',
        statusReason: disableReason.trim() || undefined,
      })
      setUserDetail((prev) => prev ? { ...prev, user: updatedUser } : prev)
      setShowDisableReason(false)
      setDisableReason('')
      fetchUsers()
      notifications.show({
        title: 'Account Disabled',
        message: `${userDetail.user.email} has been disabled.`,
        color: 'orange',
      })
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setDisabling(false)
    }
  }, [userDetail, disableReason, fetchUsers])

  const handleEnable = useCallback(async () => {
    if (!userDetail) return
    setDisabling(true)
    try {
      const updatedUser = await updateUser(userDetail.user._id, {
        status: 'active',
        statusReason: '',
      })
      setUserDetail((prev) => prev ? { ...prev, user: updatedUser } : prev)
      fetchUsers()
      notifications.show({
        title: 'Account Enabled',
        message: `${userDetail.user.email} has been re-enabled.`,
        color: 'green',
      })
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setDisabling(false)
    }
  }, [userDetail, fetchUsers])

  const handleRowClick = useCallback(
    (user: UserWithAppCount) => {
      setSelectedUserId(user._id)
      setUserDetail(null)
      openModal()
    },
    [openModal]
  )

  // Initial fetch on mount
  const didMount = useRef(false)
  if (!didMount.current) {
    didMount.current = true
    fetchUsers()
  }

  // Sync debounced search to store — setSearchQuery is stable (Zustand)
  const prevSearch = useRef(debouncedSearch)
  if (prevSearch.current !== debouncedSearch) {
    prevSearch.current = debouncedSearch
    setSearchQuery(debouncedSearch)
  }

  const handleStatusChange = useCallback(
    (value: string | null) => {
      setStatusFilter((value as UserStatus) || null)
    },
    [setStatusFilter]
  )

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(event.currentTarget.value)
    },
    []
  )

  const handleSortChange = useCallback(
    ({ columnAccessor, direction }: { columnAccessor: string; direction: 'asc' | 'desc' }) => {
      setSort(columnAccessor, direction)
    },
    [setSort]
  )

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Users</Title>
        <Button leftSection={<IconUserPlus size={16} />} onClick={handleOpenCreateUser}>
          Create User
        </Button>
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          onClose={clearError}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      <Group>
        <TextInput
          placeholder="Search by email or name..."
          leftSection={<IconSearch size={16} />}
          value={searchInput}
          onChange={handleSearchChange}
          style={{ flex: 1, maxWidth: 300 }}
        />
        <Select
          placeholder="Filter by status"
          data={STATUS_OPTIONS}
          value={statusFilter || ''}
          onChange={handleStatusChange}
          clearable={false}
          style={{ width: 160 }}
        />
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        idAccessor="_id"
        records={users}
        fetching={isLoading}
        minHeight={200}
        noRecordsIcon={<IconDatabaseOff size={24} strokeWidth={1.5} />}
        noRecordsText="No users found"
        onRowClick={({ record }) => handleRowClick(record)}
        style={{ cursor: 'pointer' }}
        sortStatus={{ columnAccessor: sortBy, direction: sortDir }}
        onSortStatusChange={handleSortChange}
        columns={[
          {
            accessor: 'email',
            title: 'Email',
            width: 250,
            sortable: true,
          },
          {
            accessor: 'name',
            title: 'Name',
            width: 180,
            sortable: true,
            render: (user) => getUserName(user),
          },
          {
            accessor: 'status',
            title: 'Status',
            width: 100,
            sortable: true,
            render: (user) => (
              <Badge color={STATUS_COLORS[user.status]} variant="light">
                {user.status}
              </Badge>
            ),
          },
          {
            accessor: 'appProfileCount',
            title: 'Apps',
            width: 80,
            textAlign: 'center',
            render: (user) => (
              <Text size="sm" c="dimmed">
                {user.appProfileCount}
              </Text>
            ),
          },
          {
            accessor: 'passkeyCount',
            title: 'Passkeys',
            width: 100,
            textAlign: 'center',
            render: (user) =>
              user.passkeyCount ? (
                <Badge color="blue" variant="light" size="sm" leftSection={<IconKey size={11} />}>
                  {user.passkeyCount}
                </Badge>
              ) : (
                <Text size="sm" c="dimmed">—</Text>
              ),
          },
          {
            accessor: 'lastActive',
            title: 'Last Active',
            width: 120,
            sortable: true,
            render: (user) => (
              <Text size="sm" c="dimmed">
                {formatDate(user.lastActive)}
              </Text>
            ),
          },
          {
            accessor: 'createdAt',
            title: 'Created',
            width: 120,
            sortable: true,
            render: (user) => (
              <Text size="sm" c="dimmed">
                {formatDate(user.createdAt)}
              </Text>
            ),
          },
        ]}
        totalRecords={total}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={setPageSize}
        paginationText={({ from, to, totalRecords }) =>
          `${from}-${to} of ${totalRecords} users`
        }
      />

      {/* User Detail Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={<Text fw={600}>User Details</Text>}
        size="960px"
      >
        {detailLoading && (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        )}

        {detailError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {detailError}
          </Alert>
        )}

        {userDetail && !detailLoading && (
          <Tabs defaultValue="overview" keepMounted={false}>
            <Tabs.List mb="md">
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab
                value="activity"
                rightSection={
                  loginActivity && loginActivity.flags.length > 0 ? (
                    <Badge size="xs" color="red" variant="filled" circle>
                      {loginActivity.flags.length}
                    </Badge>
                  ) : undefined
                }
              >
                Login Activity
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview">
          <Stack gap="md">
            {/* Identity Header */}
            <div>
              <Text fw={600} size="lg" mb={2}>
                {(() => {
                  const { firstName, lastName, displayName } = userDetail.user.profile
                  if (displayName) return displayName
                  if (firstName || lastName) return [firstName, lastName].filter(Boolean).join(' ')
                  return userDetail.user.email
                })()}
              </Text>
              <Text size="sm" c="dimmed">{userDetail.user.email}</Text>
              {(userDetail.user.profile.organization || userDetail.user.profile.jobTitle) && (
                <Text size="sm" c="dimmed">
                  {[userDetail.user.profile.organization, userDetail.user.profile.jobTitle]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              )}
            </div>

            <Divider label={<Text size="sm" fw={500}>Account</Text>} labelPosition="left" />

            <Stack gap="xs">
              <SimpleGrid cols={4} spacing="xl" verticalSpacing="sm">
                <div>
                  <Text size="sm" c="dimmed" mb={4}>Status</Text>
                  <Badge color={STATUS_COLORS[userDetail.user.status]} variant="light">
                    {userDetail.user.status}
                  </Badge>
                </div>
                <div>
                  <Text size="sm" c="dimmed" mb={4}>Last Login</Text>
                  <Text size="sm">{formatDate(userDetail.user.lastLogin)}</Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed" mb={4}>Last Active</Text>
                  <Text size="sm">{formatDate(userDetail.user.lastActive)}</Text>
                </div>
                {userDetail.user.mustChangePassword && (
                  <div>
                    <Text size="sm" c="dimmed" mb={4}>Password Reset</Text>
                    <Badge color="orange" variant="light">Required</Badge>
                  </div>
                )}
                {userDetail.user.statusReason && (
                  <div>
                    <Text size="sm" c="dimmed" mb={4}>Reason</Text>
                    <Text size="sm">{userDetail.user.statusReason}</Text>
                  </div>
                )}
              </SimpleGrid>

              {userDetail.user.failedLoginAttempts > 0 && (
                <Text size="xs" c={userDetail.user.lockedUntil && new Date(userDetail.user.lockedUntil) > new Date() ? 'red' : 'orange'}>
                  {userDetail.user.failedLoginAttempts} failed login attempt{userDetail.user.failedLoginAttempts !== 1 ? 's' : ''}
                  {userDetail.user.lockedUntil && new Date(userDetail.user.lockedUntil) > new Date() &&
                    ` · locked until ${new Date(userDetail.user.lockedUntil).toLocaleString()}`}
                </Text>
              )}

              {showDisableReason ? (
                <Stack gap="xs" mt={4}>
                  <TextInput
                    placeholder="Reason for disabling (optional)"
                    value={disableReason}
                    onChange={(e) => setDisableReason(e.currentTarget.value)}
                    size="sm"
                  />
                  <Group gap="xs">
                    <Button
                      size="xs"
                      color="red"
                      leftSection={<IconUserOff size={14} />}
                      loading={disabling}
                      onClick={handleDisable}
                    >
                      Confirm Disable
                    </Button>
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => { setShowDisableReason(false); setDisableReason('') }}
                      disabled={disabling}
                    >
                      Cancel
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <Group gap="xs" mt={4}>
                  {userDetail.user.status === 'active' && (
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      leftSection={<IconUserOff size={14} />}
                      onClick={() => setShowDisableReason(true)}
                    >
                      Disable Account
                    </Button>
                  )}
                  {userDetail.user.status === 'inactive' && (
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      leftSection={<IconUserCheck size={14} />}
                      loading={disabling}
                      onClick={handleEnable}
                    >
                      Re-enable Account
                    </Button>
                  )}
                  {(userDetail.user.failedLoginAttempts > 0 || (userDetail.user.lockedUntil && new Date(userDetail.user.lockedUntil) > new Date())) && (
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      leftSection={<IconLockOpen size={14} />}
                      loading={unlocking}
                      onClick={handleUnlock}
                    >
                      Unlock Account
                    </Button>
                  )}
                </Group>
              )}
            </Stack>

            <Divider label={<Text size="sm" fw={500}>Access Scopes</Text>} labelPosition="left" />

            <ScopeEditor
              scopes={userDetail.user.accessScopes}
              clients={clients}
              onSave={handleScopesSave}
            />

            <Group justify="space-between" align="center">
              <Divider label={<Text size="sm" fw={500}>Application Profiles</Text>} labelPosition="left" style={{ flex: 1 }} />
            </Group>

            {!addingApp && (
              <Group justify="flex-end">
                <Tooltip
                  label="All active applications are already assigned"
                  disabled={availableAppsToAdd.length > 0}
                >
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={handleOpenAddApp}
                    disabled={availableAppsToAdd.length === 0}
                  >
                    Add Application
                  </Button>
                </Tooltip>
              </Group>
            )}

            {addingApp && (
              <Paper withBorder p="xs">
                <Group align="flex-end" wrap="nowrap">
                  <Select
                    label="Application"
                    placeholder="Select application"
                    size="xs"
                    data={availableAppsToAdd.map((app) => ({ value: app.appId, label: app.name }))}
                    value={newAppId}
                    onChange={handleSelectNewApp}
                    style={{ flex: 1 }}
                  />
                  <Select
                    label="Role"
                    placeholder="Select role"
                    size="xs"
                    data={getRoleOptions(newAppId || '', newAppRole || '')}
                    value={newAppRole}
                    onChange={setNewAppRole}
                    disabled={!newAppId}
                    w={150}
                  />
                  <Button
                    size="xs"
                    onClick={handleConfirmAddApp}
                    loading={addAppLoading}
                    disabled={!newAppId || !newAppRole}
                  >
                    Add
                  </Button>
                  <Button size="xs" variant="default" onClick={handleCancelAddApp} disabled={addAppLoading}>
                    Cancel
                  </Button>
                </Group>
              </Paper>
            )}

            {userDetail.appProfiles.length === 0 ? (
              <Text c="dimmed" size="sm">
                No application access configured
              </Text>
            ) : (
              <Paper withBorder>
                <Table layout="fixed">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w="25%">App ID</Table.Th>
                      <Table.Th w="40%">Role</Table.Th>
                      <Table.Th w="35%">Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {userDetail.appProfiles.map((profile) => {
                      const roleInvalid = !isRoleValid(profile.appId, profile.role)
                      const roleOptions = getRoleOptions(profile.appId, profile.role)
                      const isUpdating = updatingProfileId === profile._id

                      return (
                        <Table.Tr key={profile._id} style={{ opacity: profile.isActive ? 1 : 0.55 }}>
                          <Table.Td>{profile.appId}</Table.Td>
                          <Table.Td>
                            <Group gap="xs">
                              {roleOptions.length > 0 ? (
                                <Select
                                  size="xs"
                                  value={profile.role}
                                  data={roleOptions}
                                  onChange={(value) =>
                                    value && handleRoleChange(profile, value)
                                  }
                                  disabled={isUpdating}
                                  error={roleInvalid}
                                  w={150}
                                />
                              ) : (
                                <Badge variant="light" size="sm">
                                  {profile.role}
                                </Badge>
                              )}
                              {roleInvalid && (
                                <IconAlertTriangle
                                  size={16}
                                  color="var(--mantine-color-orange-6)"
                                  title="Role not in application's allowed roles"
                                />
                              )}
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs" wrap="nowrap">
                              <Badge
                                color={profile.isActive ? 'green' : 'gray'}
                                variant="light"
                                size="sm"
                              >
                                {profile.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                              {profile.isActive ? (
                                <Tooltip label="Remove application">
                                  <ActionIcon
                                    size="sm"
                                    color="red"
                                    variant="subtle"
                                    disabled={isUpdating}
                                    onClick={() => handleRemoveApp(profile)}
                                  >
                                    <IconTrash size={14} />
                                  </ActionIcon>
                                </Tooltip>
                              ) : (
                                <Tooltip label="Reactivate application">
                                  <ActionIcon
                                    size="sm"
                                    color="green"
                                    variant="subtle"
                                    disabled={isUpdating}
                                    onClick={() => handleToggleProfileActive(profile, true)}
                                  >
                                    <IconRotateClockwise size={14} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}

            <Divider label={<Text size="sm" fw={500}>Active Sessions</Text>} labelPosition="left" />

            {userDetail.sessions.length === 0 ? (
              <Text c="dimmed" size="sm">
                No active sessions
              </Text>
            ) : (
              <Paper withBorder>
                <Table layout="fixed">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w="25%">App</Table.Th>
                      <Table.Th w="40%">Last Used</Table.Th>
                      <Table.Th w="35%">Created</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {userDetail.sessions.map((session) => (
                      <Table.Tr key={session._id}>
                        <Table.Td>{session.appId}</Table.Td>
                        <Table.Td>{formatDate(session.lastUsedAt)}</Table.Td>
                        <Table.Td>{formatDate(session.createdAt)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}

            <Divider label={<Text size="sm" fw={500}>Passkeys</Text>} labelPosition="left" />

            <UserPasskeysSection
              userId={userDetail.user._id}
              userLabel={userDetail.user.email}
              onCountChange={(count) =>
                setUserDetail((prev) =>
                  prev ? { ...prev, user: { ...prev.user, passkeyCount: count } } : prev
                )
              }
            />

            <Text size="xs" c="dimmed" mt="xs">
              Created {formatDate(userDetail.user.createdAt)}
              {' · '}
              Updated {formatDate(userDetail.user.updatedAt)}
            </Text>
          </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="activity">
              <LoginActivityPanel
                activity={loginActivity}
                loading={loginActivityLoading}
                error={loginActivityError}
              />
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>

      {/* Create User Modal */}
      <Modal
        opened={createUserOpened}
        onClose={closeCreateUser}
        title={<Text fw={600}>Create User</Text>}
        size="lg"
      >
        <form onSubmit={handleCreateUserSubmit}>
          <Stack gap="md">
            {createUserError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {createUserError}
              </Alert>
            )}

            <TextInput
              label="Email"
              placeholder="user@example.com"
              required
              {...createUserForm.getInputProps('email')}
            />

            <Group grow>
              <TextInput
                label="First Name"
                placeholder="Jane"
                required
                {...createUserForm.getInputProps('firstName')}
              />
              <TextInput
                label="Last Name"
                placeholder="Smith"
                required
                {...createUserForm.getInputProps('lastName')}
              />
            </Group>

            <Group grow>
              <TextInput
                label="Organization"
                placeholder="Optional"
                {...createUserForm.getInputProps('organization')}
              />
              <TextInput
                label="Job Title"
                placeholder="Optional"
                {...createUserForm.getInputProps('jobTitle')}
              />
            </Group>

            <PasswordInput
              label="Temporary Password"
              placeholder="Must be changed on first login"
              description="Min 8 chars, uppercase, lowercase, and number required"
              required
              {...createUserForm.getInputProps('password')}
            />

            {applications.length > 0 && (
              <>
                <Divider label={<Text size="sm" fw={500}>Application Access</Text>} labelPosition="left" />
                <Stack gap="xs">
                  {applications.filter((app) => app.isActive).map((app) => {
                    const sel = appSelections[app.appId] ?? { selected: false, role: app.defaultNewUserRole || (app.allowedRoles[0] ?? '') }
                    const roleOptions = app.allowedRoles.map((r) => ({ value: r, label: r }))
                    return (
                      <Group key={app.appId} align="center" wrap="nowrap">
                        <Checkbox
                          checked={sel.selected}
                          label={app.name}
                          onChange={(e) => {
                            const checked = e.currentTarget.checked
                            setAppSelections((prev) => ({
                              ...prev,
                              [app.appId]: { ...prev[app.appId], selected: checked },
                            }))
                          }}
                          style={{ flex: 1 }}
                        />
                        {sel.selected && roleOptions.length > 0 && (
                          <Select
                            size="xs"
                            placeholder="Role"
                            data={roleOptions}
                            value={sel.role}
                            onChange={(value) =>
                              setAppSelections((prev) => ({
                                ...prev,
                                [app.appId]: { ...prev[app.appId], role: value ?? '' },
                              }))
                            }
                            w={160}
                          />
                        )}
                      </Group>
                    )
                  })}
                </Stack>
              </>
            )}

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeCreateUser}>
                Cancel
              </Button>
              <Button type="submit" loading={createUserLoading}>
                Create User
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

    </Stack>
  )
}
